import {
  Configuration,
  DisplayConfiguration,
  GlobalConfiguration,
  GlobalHeaderConfiguration,
  LoginType,
  VendorLogo
} from 'shared/models/configuration';
import { IlsType } from 'shared/models/ilsType';
import { CustomerIntegrationType } from '../customer-integration/customer-integration';
import { LibraryInfoConfiguration } from 'shared/models/library-info';
import { InnReachType } from '../models/customization';
import { customDomainLoader } from '../services/custom-domain-loader';
import { nativeLanguageNamesMap } from './native-languages-map';
import { getJSON } from './utils/get-json';
import { ApplicationName } from 'app/models/customer-language';

export class ConfigurationLoader {
  public configurationPromise: Promise<Configuration>;
  public config: Configuration;
  public location: Location;
  public queryParams: {[key: string]: string};
  public availableLanguages: { code: string, name: string }[] = [
    { code: 'en-US', name: 'English (US)' },
  ];

  constructor() {
    this.location = (window as any).location;
    this.queryParams = this.location.search.slice(1).split('&')
      .map((str) => str.split('='))
      .reduce((accum, [key, value]) => Object.assign(accum, {[key]: value}), {});
  }

  public get headerConfig() {
    return this.config?.globalHeaderConfiguration || {
      title: 'Library name',
    } as GlobalHeaderConfiguration;
  }

  public get loginType(): LoginType | undefined {
    return this.config?.globalHeaderConfiguration?.loginType;
  }

  public get linkResolver() {
    return this.config?.linkResolverConfiguration;
  }

  public get logo() {
    return this.config?.logo && this.config.logo.url;
  }
  public get favicon() {
    return this.config?.favicon && this.config.favicon.url;
  }

  public get configuration(): GlobalConfiguration {
    return this.config?.configuration;
  }

  public get ilsType(): IlsType {
    return this.config?.ilsType;
  }

  public get timeoutSeconds(): number {
    return this.config?.timeoutSeconds;
  }

  public get libraryInfoConfiguration(): LibraryInfoConfiguration {
    return this.config?.libraryInfoWidget;
  }

  public get libraryInformationEnabled() {
    return this.config?.libraryInfoWidget
    && this.config.libraryInfoWidget.libraryInfo.enabled;
  }

  public get localizationConfiguration() {
    return this.config?.localizationConfiguration;
  }

  public get siteId() {
    return this.config?.siteId;
  }

  public get customerLanguages() {
    return this.config?.customerLanguages;
  }

  public get vendorsLogo(): { [key: string]: string } {
      const vendorLogoMap: { [key: string]: string } = {};
      this.config?.vendorsLogo?.forEach((vendorLogo) => {
          vendorLogoMap[vendorLogo.name] = vendorLogo.url;
        });
      return vendorLogoMap;
  }

  public get nonIntegratedConnectionsLogoMapping(): { [key: string]: VendorLogo } {
    const nonIntegratedConnectionsLogoMapping: { [key: string]: VendorLogo } = {};
    for (const key in this.config.nonIntegratedConnectionsLogoMapping) {
      if (this.config?.nonIntegratedConnectionsLogoMapping.hasOwnProperty(key)) {
        nonIntegratedConnectionsLogoMapping[key] = this.config.nonIntegratedConnectionsLogoMapping[key];
      }
    }
    return nonIntegratedConnectionsLogoMapping;
  }

  public get connectionsConfiguration() {
    return this.config?.connections;
  }

  public get displayConfiguration(): DisplayConfiguration {
    return this.config?.displayConfiguration;
  }

  public get passThroughConfig() {
    return this.config?.passthroughConnections;
  }

  public get innReachConfig() {
    return this.config?.innReachSystemConfigurations && this.config.innReachSystemConfigurations[0] || {
      systemName: '',
      type: InnReachType.WebPac,
      url: '',
      imageUrl: '',
      hideSystemName: false,
    };
  }

  public get syndeticsConnection() {
    return this.config?.syndeticsConnection;
  }

  public get syndeticsUnboundConnection() {
    return this.config?.syndeticsUnboundConnection;
  }

  public get customerIntegrationType(): CustomerIntegrationType | undefined {
    return this.config?.customerIntegrationType;
  }

  public get isRapidoConfigured(): boolean {
    return this.config?.isRapidoConfigured;
  }

  public async loadConfiguration(locale?: string) {
    if (this.configurationPromise) {
      return this.configurationPromise;
    }
    const { configurationId, logoKey, previewLanguage, faviconKey } = this.queryParams;
    const useSpecificConfiguration = configurationId && (!locale || locale === previewLanguage);
    const url = useSpecificConfiguration ?
      `api/customization/configurations-extended/${configurationId}?logoKey=${logoKey}&faviconKey=${faviconKey}` :
      'api/search-result/customization/configurations-extended/latest';
    const headers: { [key: string]: any } = {
      'api-version': useSpecificConfiguration ? '2' : '1',
    };

    if (locale) {
      headers['accept-language'] = [locale].concat(window.navigator.languages);
    }

    const domain = await customDomainLoader.loadDomain();
    const configP = getJSON<Configuration>(
      `${API_URL}/${url}`, headers, domain
    )
      .catch( () => ({ uiLocale: 'en-US' } as Configuration) );

    return this.configurationPromise = configP.then((config) => {
      this.config = config;
      if (config.customerLanguages) {
        this.availableLanguages = config.customerLanguages
        .filter((customerLanguage) => customerLanguage.enabled &&
          customerLanguage.application === ApplicationName.PATRON)
        .map((customerLanguage) => {
          const splitedLocale = customerLanguage.language.name.split('-');

          return {
            code: customerLanguage.language.name,
            name: nativeLanguageNamesMap[splitedLocale[0]].concat(splitedLocale[1] ? ` (${splitedLocale[1]})` /* istanbul ignore next */ : ''),
          };
        });
      }

      return config;
    });
  }
}

export const configurationLoader = new ConfigurationLoader();
